import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from '../../contexts/GlobalContext';
import './TerminalTab.css';

const TerminalTab: React.FC = () => {
  const { t } = useTranslation();
  const { currentDirectory } = useGlobalContext();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState<string[]>([]);
  const [commandHistory, setCommandHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeProcess, setActiveProcess] = useState<string | null>(null);
  const terminalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isInitializedRef = useRef<boolean>(false);

  // Terminal başlatma ve event listener'ları ayarlama
  useEffect(() => {
    let isMounted = true;

    const setupTerminal = async () => {
      // Eğer terminal zaten başlatılmışsa, sadece event listener'ları ayarla
      if (isInitializedRef.current) {
        return;
      }

      try {
        const result = await window.electron.ipcRenderer.invoke('terminal-start', { 
          cwd: currentDirectory 
        });
        if (result.success && isMounted) {
          setOutput(prev => [...prev, `Terminal started: ${result.shell}`, `Working directory: ${result.cwd}`, '']);
          isInitializedRef.current = true;
        }
      } catch (error) {
        console.error('Terminal başlatma hatası:', error);
      }
    };

    setupTerminal();

    // Terminal çıktısını dinle
    const unsubscribeOutput = window.electron.ipcRenderer.on('terminal-output', (data: string) => {
      if (isMounted) {
        setOutput(prev => [...prev, data]);
        if (terminalRef.current) {
          terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
      }
    });

    // Process başlangıcını dinle
    const unsubscribeProcessStart = window.electron.ipcRenderer.on('terminal-process-start', () => {
      if (isMounted) {
        setIsProcessing(true);
      }
    });

    // Process bitişini dinle
    const unsubscribeProcessEnd = window.electron.ipcRenderer.on('terminal-process-end', (code: number) => {
      if (isMounted) {
        setIsProcessing(false);
        setActiveProcess(null);
        if (code !== 0) {
          setOutput(prev => [...prev, `Process exited with code ${code}`]);
        }
      }
    });

    // Terminal çıkışını dinle
    const unsubscribeExit = window.electron.ipcRenderer.on('terminal-exit', (code: number) => {
      if (isMounted) {
        setOutput(prev => [...prev, `Terminal exited with code ${code}`]);
      }
    });

    // Cleanup
    return () => {
      isMounted = false;
      unsubscribeOutput();
      unsubscribeProcessStart();
      unsubscribeProcessEnd();
      unsubscribeExit();
      // Terminal process'ini sadece komponent tamamen unmount olduğunda sonlandır
      if (!isInitializedRef.current) {
        window.electron.ipcRenderer.invoke('terminal-kill');
      }
    };
  }, [currentDirectory]);

  // Otomatik scroll
  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [output]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  const handleClearCommand = () => {
    setOutput([]);
  };

  const executeCommand = async (command: string) => {
    if (!command.trim() || isProcessing) return;

    const trimmedCommand = command.trim().toLowerCase();
    
    // Komut geçmişini güncelle
    setCommandHistory(prev => [...prev, command]);
    setHistoryIndex(-1);
    setInput('');

    // cls veya clear komutu için özel işlem
    if (trimmedCommand === 'cls' || trimmedCommand === 'clear') {
      handleClearCommand();
      return;
    }

    // Komutu output'a ekle
    setOutput(prev => [...prev, `$ ${command}`]);
    setActiveProcess(command);

    try {
      // Komutu çalıştır - process başlangıç/bitiş olayları main process'ten gelecek
      const result = await window.electron.ipcRenderer.invoke('terminal-execute', {
        command,
        cwd: currentDirectory
      });

      // Sadece hata durumunda mesaj göster
      if (!result.success && result.error) {
        setOutput(prev => [...prev, `Error: ${result.error}`]);
      }
    } catch (error) {
      console.error('Komut çalıştırma hatası:', error);
      setOutput(prev => [...prev, `Error: ${error}`]);
      setIsProcessing(false);
      setActiveProcess(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      executeCommand(input);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex === -1 
          ? commandHistory.length - 1 
          : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setInput(commandHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex >= 0) {
        const newIndex = historyIndex + 1;
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1);
          setInput('');
        } else {
          setHistoryIndex(newIndex);
          setInput(commandHistory[newIndex]);
        }
      }
    } else if (e.key === 'c' && e.ctrlKey) {
      // Ctrl+C - komutu iptal et veya işlemi durdur
      setInput('');
      if (isProcessing) {
        setIsProcessing(false);
        setActiveProcess(null);
        window.electron.ipcRenderer.invoke('terminal-kill');
      }
      setOutput(prev => [...prev, '^C']);
    } else if (e.key === 'l' && e.ctrlKey) {
      // Ctrl+L - ekranı temizle
      e.preventDefault();
      setOutput([]);
    }
  };

  const handleContainerClick = () => {
    // Terminal container'a tıklandığında input'a focus
    inputRef.current?.focus();
  };

  return (
    <div className="terminal-tab-container" onClick={handleContainerClick}>
      <div className="terminal-tab-content" ref={terminalRef}>
        {output.map((line, index) => (
          <div key={index} className="terminal-line">
            {line}
          </div>
        ))}
        <div className="terminal-prompt">
          <span className="prompt-path">{currentDirectory || '~'}</span>
          <span className="prompt-symbol">$</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="terminal-input"
            autoFocus
            spellCheck={false}
            disabled={isProcessing}
            placeholder={isProcessing ? `Running: ${activeProcess}` : 'Type a command...'}
          />
          <span className="prompt-cursor">_</span>
        </div>
      </div>
    </div>
  );
};

export default TerminalTab;
